import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ScriptLoaderService {
  private loadedScripts: Set<string> = new Set();

  loadScript(url: string, id?: string, integrity?: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const identifier = id || url;
      // Check both our own tracking set and the live DOM. The DOM check matters because a
      // caller may remove the <script> tag (e.g. via removeScriptById) without this service
      // being the one to decide that's safe — some scripts (anything that calls
      // customElements.define at the top level, for example) can never be re-executed once
      // they've run, so re-appending and re-loading them throws even though nothing here
      // looks "loaded" from the Set alone.
      if (
        this.loadedScripts.has(identifier) ||
        document.getElementById(identifier)
      ) {
        resolve();
        return;
      }

      const scriptElement = document.createElement('script');
      scriptElement.src = url;
      if (id) {
        scriptElement.id = id;
      }
      if (integrity) {
        // Subresource Integrity requires CORS mode, hence crossOrigin alongside it.
        scriptElement.integrity = integrity;
        scriptElement.crossOrigin = 'anonymous';
      }
      scriptElement.onload = () => {
        this.loadedScripts.add(identifier);
        resolve();
      };
      scriptElement.onerror = (error) => reject(error);
      document.body.appendChild(scriptElement);
    });
  }

  removeScript(url: string): void {
    const scriptElement = document.querySelector(`script[src="${url}"]`);
    if (scriptElement) {
      scriptElement.parentNode.removeChild(scriptElement);
      this.loadedScripts.delete(url);
    }
  }

  removeScriptById(id: string): void {
    const scriptElement = document.getElementById(id);
    if (scriptElement) {
      scriptElement.parentNode.removeChild(scriptElement);
      this.loadedScripts.delete(id);
    }
  }
}
